<script lang="ts">
    import { onMount } from 'svelte';
    import { link, push } from 'svelte-spa-router';
    import { auth } from '../js/auth.js';
    import { api } from '../js/api.js';
    import { toast } from '../js/toast.js';
    import { AuthLayout, AuthPasswordInput, AuthPrimaryButton, AuthStatusCallout } from './app';

    export let params: { token?: string };

    let token = '';
    let password = '';
    let c_password = '';
    let errors: Record<string, string> = {};

    let userEmail = '';
    let tokenValid = true;
    let tokenErrorMessage = '';
    let isRequestingNewLink = false;
    let tokenNotFound = false;
    let showEmailInput = false;
    let isNewUser = false;
    let submitting = false;
    let emailEl: HTMLInputElement;

    $: if (showEmailInput && emailEl) emailEl.focus();

    onMount(async () => {
        if (params.token) {
            token = params.token;
            const user: any = await auth.getUserByToken(token);
            if (user?.success && user?.data?.email) {
                userEmail = user.data.email;
                isNewUser = !user.data.onboarding_completed;
                tokenValid = true;
                tokenNotFound = false;
            } else {
                tokenValid = false;
                tokenErrorMessage = user?.message || 'This invitation link is invalid, expired, or has already been used.';
                if (user?.message && user.message.includes('not found')) {
                    tokenNotFound = true;
                } else {
                    tokenNotFound = false;
                    if (user?.data?.user?.email) userEmail = user.data.user.email;
                    else showEmailInput = true;
                }
            }
        } else {
            tokenValid = false;
            tokenErrorMessage = 'Invalid reset link.';
        }
    });

    async function requestNewLink() {
        if (!userEmail) { errors.email = 'Email address is required'; return; }
        if (!validateEmail(userEmail)) { errors.email = 'Please enter a valid email address'; return; }
        errors.email = '';
        isRequestingNewLink = true;
        await auth.resetPasswordSend(userEmail);
        isRequestingNewLink = false;
        toast.showNotification($auth.success, $auth.message, 'top');
        if ($auth.success) push('/login');
    }

    function validateEmail(email: string) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    $: if (errors.email && userEmail && validateEmail(userEmail)) errors.email = '';
    $: passwordsMismatch = c_password.length > 0 && password !== c_password;

    async function resetPassword() {
        if (!validateForm()) return;
        if (password !== c_password) {
            errors.c_password = 'Passwords do not match';
            return;
        }
        submitting = true;
        try {
            if (await auth.updatePassword(token, password, c_password)) {
                if ($auth.success) {
                    if (await auth.login(userEmail, password)) {
                        await api.getResourceLists();
                        toast.showNotification($auth.success, isNewUser ? 'Password set. You have been logged in.' : 'Password reset. You have been logged in.', 'top');
                        if (auth.userIsAdmin()) push('/dashboard-admin');
                        else if (!$auth.user?.onboarding_completed) push('/onboarding');
                        else push('/dashboard');
                    } else {
                        toast.showNotification($auth.success, $auth.message, 'top');
                        push('/login');
                    }
                } else {
                    toast.showNotification($auth.success, $auth.message, 'top');
                }
            }
        } finally {
            submitting = false;
        }
    }

    function validateForm() {
        errors = {};
        let valid = true;
        if (!c_password) { errors.c_password = 'Confirm password is required'; valid = false; }
        if (!password) { errors.password = 'Password is required'; valid = false; }
        else if (password.length < 6) { errors.password = 'Password must be at least 6 characters'; valid = false; }
        if (password && c_password && password !== c_password) {
            errors.c_password = 'Passwords do not match';
            valid = false;
        }
        return valid;
    }

    $: if (errors.c_password && c_password && password === c_password) errors.c_password = '';
    $: if (errors.password && password && password.length >= 6) errors.password = '';
</script>

{#if !tokenValid}
    <AuthLayout title="Link expired or invalid" subtitle={tokenErrorMessage}>
        <AuthStatusCallout tone="warn" icon="exclamation triangle" title="This link can't be used">
            <p>
                {#if userEmail}
                    Request a new password reset link for <strong>{userEmail}</strong>.
                {:else}
                    Enter your email address below to receive a new password reset link.
                {/if}
            </p>
        </AuthStatusCallout>

        {#if tokenNotFound}
            <div class="auth-actions-stack">
                <AuthPrimaryButton type="button" on:click={() => push('/login')}>
                    Back to sign in
                </AuthPrimaryButton>
                <button type="button" class="auth-secondary-button" on:click={() => push('/reset-password')}>
                    Request new password reset
                </button>
            </div>
        {:else if showEmailInput}
            <form class="auth-form" on:submit|preventDefault={requestNewLink} novalidate>
                <div class="auth-form-field" class:error={!!errors.email}>
                    <label class="auth-form-label" for="request-email">Email</label>
                    <input
                        bind:this={emailEl}
                        id="request-email"
                        class="auth-form-input"
                        type="email"
                        inputmode="email"
                        autocomplete="email"
                        spellcheck="false"
                        bind:value={userEmail}
                        placeholder="you@company.com"
                        aria-invalid={errors.email ? 'true' : 'false'}
                    />
                    {#if errors.email}<span class="auth-error-message" role="alert">{errors.email}</span>{/if}
                </div>
                <AuthPrimaryButton loading={isRequestingNewLink} loadingLabel="Sending…">
                    Send new reset link
                </AuthPrimaryButton>
            </form>
        {:else if userEmail}
            <AuthPrimaryButton type="button" loading={isRequestingNewLink} loadingLabel="Sending…" on:click={requestNewLink}>
                Send new reset link
            </AuthPrimaryButton>
        {/if}

        <p slot="footer">
            {#if !tokenNotFound}
                Remember your password?
                <a use:link href="/login" class="auth-inline-link">Back to sign in</a>
            {/if}
        </p>
    </AuthLayout>
{:else}
    <AuthLayout
        title={isNewUser ? 'Set your password' : 'Reset your password'}
        subtitle={`${isNewUser ? 'Create a password for' : 'Enter a new password for'} ${userEmail}`}
    >
        <form class="auth-form" on:submit|preventDefault={resetPassword} novalidate>
            <div class="auth-form-field">
                <label class="auth-form-label" for="email">Email</label>
                <input id="email" class="auth-form-input" type="email" value={userEmail} disabled readonly aria-disabled="true" />
            </div>

            <AuthPasswordInput
                id="password"
                label={isNewUser ? 'Password' : 'New password'}
                bind:value={password}
                placeholder={isNewUser ? 'Enter your password' : 'Enter your new password'}
                autocomplete="new-password"
                error={errors.password}
                autofocus
            />

            <AuthPasswordInput
                id="c_password"
                label="Confirm password"
                bind:value={c_password}
                placeholder="Confirm your new password"
                autocomplete="new-password"
                error={errors.c_password || (passwordsMismatch ? 'Passwords do not match' : '')}
            />

            <AuthPrimaryButton loading={submitting} loadingLabel={isNewUser ? 'Setting password…' : 'Resetting…'}>
                {isNewUser ? 'Set password' : 'Reset password'}
            </AuthPrimaryButton>
        </form>

        <p slot="footer">
            Remember your password?
            <a use:link href="/login" class="auth-inline-link">Back to sign in</a>
        </p>
    </AuthLayout>
{/if}
